import { Injectable, inject } from '@angular/core';
import { Firestore, collection, doc, getDocs, getDoc, query, where, setDoc, deleteDoc, Unsubscribe, onSnapshot } from '@angular/fire/firestore';
import { Promotion, PromotionType } from '../models/promotion.model';
import { getISTDateString } from '../utils/date.utils';
import { readCache, writeCache } from './cache.utils';

@Injectable({
  providedIn: 'root'
})
export class PromotionService {
  private firestore = inject(Firestore);

  private promoCollection = collection(this.firestore, 'promotions');

  private static readonly CACHE_KEY = 'promos_cache';
  private static readonly CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 min

  private toDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }

  private mapPromotion(id: string, data: any): Promotion {
    return {
      id,
      title: data.title || '',
      subtitle: data.subtitle,
      bannerImage: data.bannerImage,
      bannerVideo: data.bannerVideo,
      gradient: data.gradient,
      type: data.type || 'hero',
      couponCode: data.couponCode,
      ctaText: data.ctaText,
      ctaLink: data.ctaLink,
      priority: data.priority ?? 0,
      isActive: data.isActive ?? true,
      startDate: this.toDate(data.startDate) || new Date(),
      endDate: this.toDate(data.endDate) || new Date(),
      createdAt: this.toDate(data.createdAt),
      updatedAt: this.toDate(data.updatedAt),
    };
  }

  async getAllPromotions(): Promise<Promotion[]> {
    try {
      const snapshot = await getDocs(this.promoCollection);
      const promos = snapshot.docs.map(doc => this.mapPromotion(doc.id, doc.data()));
      return promos.sort((a, b) => a.priority - b.priority || (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
    } catch (error) {
      console.error('Error fetching promotions:', error);
      return [];
    }
  }

  async getPromotionById(id: string): Promise<Promotion | null> {
    try {
      const docRef = doc(this.firestore, 'promotions', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return this.mapPromotion(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching promotion:', error);
      return null;
    }
  }

  private removeUndefined<T extends Record<string, any>>(obj: T): T {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined)
    ) as T;
  }

  async createPromotion(data: Omit<Promotion, 'id' | 'createdAt' | 'updatedAt'>): Promise<boolean> {
    try {
      const docRef = doc(this.promoCollection);
      await setDoc(docRef, {
        ...this.removeUndefined(data),
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return true;
    } catch (error) {
      console.error('Error creating promotion:', error);
      return false;
    }
  }

  async updatePromotion(id: string, data: Partial<Promotion>): Promise<boolean> {
    try {
      const docRef = doc(this.firestore, 'promotions', id);
      await setDoc(docRef, { ...this.removeUndefined(data), updatedAt: new Date() }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating promotion:', error);
      return false;
    }
  }

  async deletePromotion(id: string): Promise<boolean> {
    try {
      const docRef = doc(this.firestore, 'promotions', id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting promotion:', error);
      return false;
    }
  }

  subscribeActivePromotions(type: PromotionType, onUpdate: (promotions: Promotion[]) => void): Unsubscribe {
    // Cache-first: hydrate the caller synchronously from localStorage so the
    // hero/announcement/popup renders instantly on cold/PWA opens, before the
    // Firestore listener reconnects. The cache is keyed by type and refreshed
    // by every onSnapshot callback below.
    const allCached = readCache<Record<string, Promotion[]>>(PromotionService.CACHE_KEY, PromotionService.CACHE_MAX_AGE_MS);
    if (allCached && Array.isArray(allCached[type]) && allCached[type].length > 0) {
      console.log(`[PromotionService] Hydrated ${allCached[type].length} ${type} promos from cache instantly.`);
      onUpdate(allCached[type]);
    }

    const q = query(this.promoCollection, where('isActive', '==', true));

    return onSnapshot(q, (snapshot) => {
      const todayStr = getISTDateString();
      const promos = snapshot.docs
        .map(doc => this.mapPromotion(doc.id, doc.data()))
        .filter(p => {
          const startStr = getISTDateString(p.startDate);
          const endStr = getISTDateString(p.endDate);
          return todayStr >= startStr && todayStr <= endStr;
        })
        .sort((a, b) => a.priority - b.priority);

      // Group by type before publishing — we cache all three regions under one
      // localStorage key so a single boot-time read hydrates hero/announcement/popup.
      const byType: Record<string, Promotion[]> = allCached ?? {};
      for (const t of ['hero', 'announcement', 'popup'] as PromotionType[]) {
        byType[t] = promos.filter(p => p.type === t);
      }
      writeCache(PromotionService.CACHE_KEY, byType);

      onUpdate(promos.filter(p => p.type === type));
    }, (error) => {
      console.error('Error in promotions listener:', error);
      onUpdate([]);
    });
  }
}
